import React, { useState, useEffect } from "react";
import DataGrid from "./DataGrid/DataGrid.tsx";
import { AgentReportMappingEnum } from "../../enum/normalization-mapping.enum.ts";
import moment from "moment";

interface AgentReportDataGridProps {
  policies: any[];
  isLoading: boolean;
  selectedPolicies: [string, number][];
  setSelectedPolicies: React.Dispatch<React.SetStateAction<[string, number][]>>;
  formState?: {
    fromDate: string;
    toDate: string;
    company: string;
    policyType: string;
    agent: string;
  };
  reportState?: {
    reportNumber: string;
    reportDate: string;
  };
  labelMaps?: {
    agents: { [key: string]: string };
    companies: { [key: string]: string };
    policyTypes: { [key: string]: string };
  };
}

const SEPARATOR = ",";

const AgentReportDataGrid: React.FC<AgentReportDataGridProps> = ({
  policies,
  selectedPolicies,
  setSelectedPolicies,
  isLoading,
  formState,
  reportState,
  labelMaps,
}) => {
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  useEffect(() => {
    if (policies.length > 0) {
      const allSelected = policies.every((policy) =>
        selectedPolicies.some(
          ([polId, payNum]) =>
            polId === policy[AgentReportMappingEnum.Pol_ID] &&
            payNum === policy[AgentReportMappingEnum.Pay_Num]
        )
      );
      setSelectAllChecked(allSelected);
    } else {
      setSelectAllChecked(false);
    }
  }, [policies, selectedPolicies]);

  const handleSelectAll = (isChecked: boolean) => {
    setSelectAllChecked(isChecked);
    setSelectedPolicies(
      isChecked
        ? policies.map((policy) => [
            policy[AgentReportMappingEnum.Pol_ID],
            policy[AgentReportMappingEnum.Pay_Num],
          ])
        : []
    );
  };

  const handleRowSelection = (policy: any, isChecked: boolean) => {
    setSelectedPolicies((prev) => {
      const updated = [...prev];
      const entry: [string, number] = [
        policy[AgentReportMappingEnum.Pol_ID],
        policy[AgentReportMappingEnum.Pay_Num],
      ];
      const index = updated.findIndex(
        ([polId, payNum]) => polId === entry[0] && payNum === entry[1]
      );
      if (isChecked && index === -1) {
        updated.push(entry);
      } else if (!isChecked && index !== -1) {
        updated.splice(index, 1);
      }
      return updated;
    });
  };

  const columns = [
    {
      name: "selected",
      header: (
        <input
          type="checkbox"
          checked={selectAllChecked}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      render: ({ data }) => (
        <input
          type="checkbox"
          checked={selectedPolicies.some(
            ([polId, payNum]) =>
              polId === data[AgentReportMappingEnum.Pol_ID] &&
              payNum === data[AgentReportMappingEnum.Pay_Num]
          )}
          onChange={(e) => handleRowSelection(data, e.target.checked)}
        />
      ),
      minWidth: 50,
      maxWidth: 50,
      type: "checkbox",
      sortable: false,
      filterable: false,
      enableColumnFilterContextMenu: false,
    },
    {
      name: AgentReportMappingEnum.Pol_Num,
      header: "№ Полица",
      minWidth: 140,
      type: "string",
    },
    {
      name: AgentReportMappingEnum.InsComp,
      header: "ЗК",
      minWidth: 150,
      type: "string",
    },
    {
      name: AgentReportMappingEnum.Pol_Kind,
      header: "Вид застраховка",
      minWidth: 150,
      type: "string",
    },
    {
      name: AgentReportMappingEnum.Sum_all,
      header: "Застрахователна премия",
      minWidth: 150,
      type: "number",
    },
    {
      name: AgentReportMappingEnum.Commission,
      header: (
        <div className="whitespace-normal break-words px-2 text-center">
          Комисионна
          <br />
          Агент (%)
        </div>
      ),
      overflow: "hidden",
      minWidth: 120,
      type: "number",
    },
    {
      name: AgentReportMappingEnum.Sum_Comm,
      header: "Комисионна Агент",
      minWidth: 120,
      type: "number",
    },
    {
      name: AgentReportMappingEnum.Sum_GF,
      header: "ГФ Сума",
      minWidth: 100,
      type: "number",
    },
    {
      name: AgentReportMappingEnum.Pay_Num,
      header: "Номер падеж",
      minWidth: 100,
      type: "number",
    },
  ];

  const handleExportCSV = () => {
    const lines: string[] = [];

    // ── Section 1: Filter parameters ──────────────────────────────
    lines.push("ПАРАМЕТРИ НА СПРАВКАТА" + SEPARATOR);
    lines.push(`От дата${SEPARATOR}${formState?.fromDate ?? ""}`);
    lines.push(`До дата${SEPARATOR}${formState?.toDate ?? ""}`);
    lines.push(
      `Агент${SEPARATOR}${
        labelMaps?.agents[formState?.agent] ?? formState?.agent ?? ""
      }`
    );
    lines.push(
      `ЗК${SEPARATOR}${
        labelMaps?.companies[formState?.company] ?? formState?.company ?? ""
      }`
    );
    lines.push(
      `Вид Полица${SEPARATOR}${
        labelMaps?.policyTypes[formState?.policyType] ??
        formState?.policyType ??
        ""
      }`
    );
    lines.push(`Номер на отчет${SEPARATOR}${reportState?.reportNumber ?? ""}`);
    lines.push(`Дата на отчет${SEPARATOR}${reportState?.reportDate ?? ""}`);
    lines.push("");

    // ── Section 2: Policy data ─────────────────────────────────────
    const exportColumns = columns.filter((c) => c.name !== "selected");
    lines.push("ПОЛИЦИ" + SEPARATOR);
    lines.push(
      exportColumns
        .map((c) =>
          typeof c.header === "string" ? c.header : "Комисионна Агент (%)"
        )
        .join(SEPARATOR)
    );

    const exportPolicies =
      selectedPolicies.length > 0
        ? policies.filter((policy) =>
            selectedPolicies.some(
              ([polId, payNum]) =>
                polId === policy[AgentReportMappingEnum.Pol_ID] &&
                payNum === policy[AgentReportMappingEnum.Pay_Num]
            )
          )
        : policies;

    exportPolicies.forEach((policy) => {
      const row = exportColumns
        .map((c) => {
          const val = policy[c.name] ?? "";
          const str = String(val);
          return str.includes(SEPARATOR) || str.includes("\n")
            ? `"${str.replace(/"/g, '""')}"`
            : str;
        })
        .join(SEPARATOR);
      lines.push(row);
    });

    const blob = new Blob(["\uFEFF" + lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "agent_report.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <DataGrid
      columns={columns}
      dataSource={policies}
      isLoading={isLoading}
      gridTitle="Справка за агенти"
      idProperty={AgentReportMappingEnum.Pol_ID}
      csvFilename={`Справка-Агенти-${moment().format("DD-MM-YYYY")}.csv`}
      icon="fa-kit fa-regular-file-circle-question"
      editable={false}
      onExportCSV={handleExportCSV}
    />
  );
};

export default AgentReportDataGrid;
